import { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip, TagPills, ConfidenceBadge } from "@/components/Bits";
import UploadOverlay from "@/components/UploadOverlay";
import ReconcileModal from "@/components/ReconcileModal";
import ComboSelect from "@/components/ComboSelect";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { UploadSimple, MagicWand, ListChecks, DownloadSimple, Bank } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import ChallanPostDialog from "@/components/ChallanPostDialog";

export default function BankUpload() {
    const { user } = useAuth();
    const [statements, setStatements] = useState([]);
    const [lines, setLines] = useState([]);
    const [totalLines, setTotalLines] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [bank, setBank] = useState("");
    const [banks, setBanks] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [bankFilter, setBankFilter] = useState("");
    const [busy, setBusy] = useState(false);
    const [uploadName, setUploadName] = useState("");
    const [suggesting, setSuggesting] = useState(false);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [reconcileIds, setReconcileIds] = useState([]);
    const [reconcileLabel, setReconcileLabel] = useState("");
    const [loadingLines, setLoadingLines] = useState(false);
    const [bankWarning, setBankWarning] = useState(null);
    const [tdsParties, setTdsParties] = useState([]);
    const [challanLine, setChallanLine] = useState(null);

    const load = async () => {
        setLoadingLines(true);
        try {
            const [st, l, led, bnk, locs, deps, tds] = await Promise.all([
                api.get("/bank-statements"),
                api.get("/bank-lines", { params: { status: statusFilter || undefined, bank: bankFilter || undefined, limit: pageSize, offset: 0 }}),
                api.get("/ledgers"),
                api.get("/banks"),
                api.get("/masters/locations"),
                api.get("/masters/departments"),
                api.get("/tds-parties", { params: { active_only: true } }).catch(() => ({ data: [] })),
            ]);
            setStatements(st.data);
            setLines(l.data.items || []); setTotalLines(l.data.total || 0);
            setLedgers(led.data);
            // Exclude FIAT BANK from statement-upload targets (it's cash — no statements)
            const uploadable = bnk.data.filter((b) => !/fiat/i.test(b.name));
            setBanks(uploadable);
            if (uploadable.length && !bank) setBank(uploadable[0].name);
            setLocations(locs.data); setDepartments(deps.data);
            setTdsParties(tds.data);
        } finally { setLoadingLines(false); }
    };
    useEffect(() => { load(); }, [statusFilter, bankFilter, pageSize]);

    const addNewBank = async () => {
        const name = window.prompt("Bank account name (e.g. 'HDFC Bank Account')");
        if (!name || !name.trim()) return;
        try {
            const { data } = await api.post("/banks", { name: name.trim() });
            toast.success(`Added ${data.name}`);
            await load();
            setBank(data.name);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        setUploadName(file.name);
        try {
            const fd = new FormData(); fd.append("file", file); fd.append("bank", bank);
            const { data } = await api.post("/bank-statements/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
            const parts = [
                `${data.inserted} new line${data.inserted === 1 ? "" : "s"} added`,
                `${data.duplicates} already existed (skipped)`,
                `${data.interbank_matched_pairs} inter-bank pair${data.interbank_matched_pairs === 1 ? "" : "s"} netted`,
            ];
            if (data.inserted > 0) toast.success(parts.join(" · "));
            else toast.info(parts.join(" · ") + " — nothing new to add");
            // Bank mismatch warning — non-blocking
            if (data.bank_mismatch && data.detected_bank) {
                setBankWarning({
                    selected: data.bank,
                    detected: data.detected_bank,
                    stmt_id: data.statement_id,
                    new_line_ids: data.new_line_ids || [],
                });
            } else {
                setBankWarning(null);
            }
            load();
            // Auto-open reconciliation modal for the newly inserted lines
            if (data.new_line_ids && data.new_line_ids.length > 0 && !data.bank_mismatch) {
                setReconcileIds(data.new_line_ids);
                setReconcileLabel(`${data.bank} · ${file.name}`);
                setReconcileOpen(true);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); setUploadName(""); }
    };

    const undoBadUpload = async () => {
        // Discard the freshly-inserted lines from a mis-labelled upload so the user
        // can re-upload with the correct bank selected.
        if (!bankWarning?.new_line_ids?.length) { setBankWarning(null); return; }
        try {
            const { data } = await api.post("/bank-lines/discard", { line_ids: bankWarning.new_line_ids });
            toast.info(`Undone — ${data.discarded} line${data.discarded === 1 ? "" : "s"} removed. Reselect the bank and re-upload.`);
            setBankWarning(null);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const keepDespiteMismatch = () => {
        // User overrides — proceed to reconcile the inserted lines as-is
        if (bankWarning?.new_line_ids?.length) {
            setReconcileIds(bankWarning.new_line_ids);
            setReconcileLabel(`${bankWarning.selected} (override)`);
            setReconcileOpen(true);
        }
        setBankWarning(null);
    };

    const openPendingReconcile = async () => {
        // fetch all unmatched + mapped (i.e. not yet posted, not inter-bank) lines and open modal
        const { data } = await api.get("/bank-lines", { params: { limit: 2000, offset: 0 }});
        const items = data.items || [];
        const pending = items.filter((r) => r.status !== "posted" && r.status !== "inter_bank");
        if (pending.length === 0) { toast.info("Nothing pending to reconcile"); return; }
        setReconcileIds(pending.map((r) => r.id));
        setReconcileLabel(`All pending (${pending.length})`);
        setReconcileOpen(true);
    };

    const suggestAll = async () => {
        setSuggesting(true);
        try {
            const { data } = await api.post("/bank-lines/suggest-all");
            toast.success(`Suggested for ${data.processed} lines`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSuggesting(false); }
    };
    const suggestOne = async (id) => {
        try { await api.post(`/bank-lines/${id}/suggest`); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const confirm = async (line, ledgerId, dept, location, gross, tdsPartyId) => {
        const effTds = tdsPartyId && tdsPartyId !== "__none__" ? tdsPartyId : null;
        try {
            await api.post(`/bank-lines/${line.id}/confirm`, {
                ledger_id: effTds ? null : ledgerId,
                tds_party_id: effTds,
                dept: dept || null, location: location || null,
                gateway_gross_amount: gross ? Number(gross) : null,
            });
            toast.success(effTds ? "Posted with TDS split" : "Posted"); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const reverse = async (line) => {
        const reason = window.prompt("Why are you reversing this posting?", "re-map");
        if (!reason) return;
        try {
            await api.post(`/bank-lines/${line.id}/reverse`, null, { params: { reason }});
            toast.success("Reversed — you can now map to a different ledger");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const downloadStatement = async (s) => {
        if (!s.document_id) return;
        try {
            const res = await api.get(`/documents/${s.document_id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${s.bank}_statement_${s.from_date || s.uploaded_at?.slice(0, 10)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 3000);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <UploadOverlay open={busy} filename={uploadName} kind="bank" />
            <ReconcileModal
                open={reconcileOpen}
                onClose={() => setReconcileOpen(false)}
                onDone={load}
                lineIds={reconcileIds}
                ledgers={ledgers}
                statementLabel={reconcileLabel}
            />
            <ChallanPostDialog
                open={!!challanLine}
                line={challanLine}
                onClose={() => setChallanLine(null)}
                onPosted={() => { setChallanLine(null); load(); }}
            />
            <PageHeader title="Bank statements"
                sub="Upload weekly dumps from any bank. Duplicates are auto-skipped; inter-bank transfers get netted. Hard cash goes to FIAT BANK — use the Cash page for those."
                right={
                    <>
                        <Select value={bank} onValueChange={(v) => v === "__new__" ? addNewBank() : setBank(v)}>
                            <SelectTrigger className="w-48 h-9" data-testid="bank-select"><SelectValue placeholder="Choose bank" /></SelectTrigger>
                            <SelectContent>
                                {banks.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                                <SelectItem value="__new__" className="font-medium" data-testid="bank-add-new">+ Add new bank…</SelectItem>
                            </SelectContent>
                        </Select>
                        <label className={`cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
                            <input type="file" accept=".csv,.xlsx,.xls,application/pdf,image/*" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="bank-upload-input" />
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm text-white" style={{ background: "var(--se-brand)" }} data-testid="bank-upload-btn">
                                <UploadSimple size={16} /> {busy ? "Uploading…" : "Upload statement"}
                            </span>
                        </label>
                        <Button variant="outline" onClick={suggestAll} disabled={suggesting} data-testid="suggest-all">
                            <MagicWand size={14} className="mr-2" /> {suggesting ? "Thinking…" : "Suggest all"}
                        </Button>
                        <Button variant="outline" onClick={openPendingReconcile} data-testid="open-reconcile">
                            <ListChecks size={14} className="mr-2" /> Reconcile pending
                        </Button>
                    </>
                } />
            <div className="p-8 space-y-6">
                {bankWarning ? (
                    <div className="border rounded-sm p-4 text-sm flex items-start justify-between gap-4"
                         style={{ background: "var(--se-warn-bg)", color: "var(--se-warn-text)", borderColor: "#f0d8b6" }}
                         data-testid="bank-mismatch-warning">
                        <div>
                            <div className="font-semibold">Bank mismatch — looks like this file is from <b>{bankWarning.detected}</b>, but you selected <b>{bankWarning.selected}</b>.</div>
                            <div className="text-xs mt-1">The lines have been imported under <b>{bankWarning.selected}</b>. If that was a mistake, click Undo to remove them and re-upload with the right bank.</div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline" onClick={undoBadUpload} data-testid="mismatch-undo">Undo upload</Button>
                            <Button size="sm" onClick={keepDespiteMismatch} style={{ background: "var(--se-brand)", color: "#fff" }} data-testid="mismatch-keep">Keep &amp; reconcile</Button>
                        </div>
                    </div>
                ) : null}

                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg">Statements</div>
                        <div className="text-xs text-stone-500">{statements.length} uploaded</div>
                    </div>
                    <table className="se-table">
                        <thead><tr><th>Bank</th><th>From</th><th>To</th><th className="text-right">Inserted</th><th className="text-right">Duplicates</th><th>Uploaded</th><th>File</th></tr></thead>
                        <tbody>
                            {statements.map((s) => (
                                <tr key={s.id}>
                                    <td>{s.bank}</td>
                                    <td className="font-mono text-xs">{s.from_date || "—"}</td>
                                    <td className="font-mono text-xs">{s.to_date || "—"}</td>
                                    <td className="se-num">{s.inserted}</td>
                                    <td className="se-num">{s.duplicates}</td>
                                    <td className="text-xs text-stone-500">{s.uploaded_at?.slice(0, 16).replace("T", " ")}</td>
                                    <td>
                                        {s.document_id ? (
                                            <Button size="sm" variant="outline" onClick={() => downloadStatement(s)} data-testid={`stmt-download-${s.id}`}>
                                                <DownloadSimple size={12} className="mr-1" /> Download
                                            </Button>
                                        ) : <span className="text-xs text-stone-400">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--se-border)" }}>
                        <div className="font-serif text-lg">Bank lines</div>
                        <div className="flex gap-2">
                            <select value={bankFilter} onChange={(e) => setBankFilter(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white" data-testid="filter-bank">
                                <option value="">All banks</option><option value="SBI">SBI</option><option value="ICICI">ICICI</option>
                            </select>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white" data-testid="filter-line-status">
                                <option value="">All statuses</option>
                                <option value="unmatched">Unmatched</option>
                                <option value="mapped">AI-mapped</option>
                                <option value="inter_bank">Inter-bank</option>
                                <option value="gateway_shortfall">Gateway shortfall</option>
                                <option value="posted">Posted</option>
                            </select>
                        </div>
                    </div>
                    <BankLinesTable lines={lines} ledgers={ledgers} locations={locations} departments={departments} tdsParties={tdsParties} onSuggest={suggestOne} onConfirm={confirm} onReverse={reverse} onChallan={setChallanLine} canPost={user?.role === "founder" || user?.role === "accountant"} />
                    <div className="px-5 py-3 border-t flex items-center justify-between text-xs text-stone-500" style={{ borderColor: "var(--se-border)" }}>
                        <div data-testid="bank-lines-count">
                            Showing {lines.length} of {totalLines} lines{loadingLines ? " · loading…" : ""}
                        </div>
                        {lines.length < totalLines ? (
                            <Button variant="outline" size="sm" onClick={() => setPageSize((p) => p + 50)} disabled={loadingLines} data-testid="load-more">
                                Load 50 more
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
}

function BankLinesTable({ lines, ledgers, locations, departments, tdsParties, onSuggest, onConfirm, onReverse, onChallan, canPost }) {
    return (
        <div className="overflow-x-auto">
            <table className="se-table" data-testid="bank-lines-table">
                <thead><tr>
                    <th>Bank</th><th>Date</th><th>Narration</th><th className="text-right">Amount</th>
                    <th>Status</th><th>Suggested / Map to</th><th>Post</th>
                </tr></thead>
                <tbody>
                    {lines.length === 0 ? <tr><td colSpan="7" className="text-stone-500 py-8 text-center">No bank lines match this filter.</td></tr> :
                        lines.map((b) => <BankLineRow key={b.id} b={b} ledgers={ledgers} locations={locations} departments={departments} tdsParties={tdsParties} onSuggest={onSuggest} onConfirm={onConfirm} onReverse={onReverse} onChallan={onChallan} canPost={canPost} />)
                    }
                </tbody>
            </table>
        </div>
    );
}

function BankLineRow({ b, ledgers, locations, departments, tdsParties, onSuggest, onConfirm, onReverse, onChallan, canPost }) {
    const [ledger, setLedger] = useState(b.mapped_ledger_id || b.suggested_ledger_id || "");
    const [dept, setDept] = useState(b.dept || "");
    const [loc, setLoc] = useState(b.location || "");
    const [gross, setGross] = useState("");
    const [tdsPartyId, setTdsPartyId] = useState("");
    const [expanded, setExpanded] = useState(false);
    const isPosted = b.status === "posted";
    const isInter = b.status === "inter_bank";
    const isGateway = b.status === "gateway_shortfall";
    const isDebit = Number(b.debit) > 0;
    const selectedParty = tdsParties?.find((p) => p.id === tdsPartyId);
    // Live preview of the split
    let tdsPreview = null;
    if (selectedParty && isDebit) {
        const net = Number(b.debit);
        const rate = Number(selectedParty.rate_pct) / 100;
        const grossAmt = +(net / (1 - rate)).toFixed(2);
        tdsPreview = { gross: grossAmt, tds: +(grossAmt - net).toFixed(2), net, rate: selectedParty.rate_pct };
    }
    return (
        <tr data-testid={`bl-${b.id}`}>
            <td>{b.bank}</td>
            <td className="font-mono text-xs">{b.date}</td>
            <td>
                <div className="text-sm max-w-[280px] truncate" title={b.narration}>{b.narration}</div>
                {b.ref ? <div className="text-xs text-stone-500">{b.ref}</div> : null}
                {b.suggested_reason ? <div className="text-xs text-stone-400 italic">{b.suggested_reason}</div> : null}
            </td>
            <td className="se-num">
                {b.credit ? <span style={{ color: "#1e4620" }}>+ ₹{inr(b.credit)}</span> : null}
                {b.debit ? <span style={{ color: "#991b1b" }}>- ₹{inr(b.debit)}</span> : null}
            </td>
            <td><StatusChip status={b.status} />{b.confidence != null ? <span className="ml-1"><ConfidenceBadge value={b.confidence} /></span> : null}</td>
            <td style={{ minWidth: 220 }}>
                {isInter ? <span className="text-xs text-stone-500 italic">Netted with sibling line — no posting needed</span> :
                    isPosted ? <span className="text-xs text-stone-500">Posted to {ledgers.find(l => l.id === b.mapped_ledger_id)?.name || "—"}</span> :
                    <ComboSelect
                        value={ledger}
                        onValueChange={setLedger}
                        options={ledgers.map((l) => ({ id: l.id, name: l.name, hint: l.group_name }))}
                        placeholder="Choose ledger"
                        testId={`ledger-select-${b.id}`}
                        size="sm"
                    />
                }
            </td>
            <td>
                {(!isPosted && !isInter) ? (
                    <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => onSuggest(b.id)} data-testid={`suggest-${b.id}`}>Suggest</Button>
                            <Button size="sm" onClick={() => onConfirm(b, ledger, dept, loc, gross, tdsPartyId)}
                                disabled={(!ledger && !tdsPartyId) || !canPost}
                                style={{ background: "var(--se-brand)", color: "#fff" }} data-testid={`confirm-${b.id}`}>
                                {tdsPartyId ? "Post + TDS" : "Post"}
                            </Button>
                            {isDebit && canPost ? (
                                <Button variant="outline" size="sm" onClick={() => onChallan?.(b)} title="Settle a TDS / GST liability with this bank line" data-testid={`challan-${b.id}`}>
                                    <Bank size={12} className="mr-1" /> Challan
                                </Button>
                            ) : null}
                            <Button variant="ghost" size="sm" onClick={() => setExpanded((x) => !x)}>{expanded ? "−" : "+"}</Button>
                        </div>
                        {expanded ? (
                            <div className="mt-1 space-y-1 border-t pt-2" style={{ borderColor: "var(--se-border)" }}>
                                <div className="flex gap-1">
                                    <Select value={dept} onValueChange={setDept}><SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Dept" /></SelectTrigger><SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select>
                                    <Select value={loc} onValueChange={setLoc}><SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Location" /></SelectTrigger><SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
                                </div>
                                {isDebit && tdsParties?.length > 0 ? (
                                    <div className="flex items-center gap-1">
                                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">TDS on</Label>
                                        <Select value={tdsPartyId} onValueChange={setTdsPartyId}>
                                            <SelectTrigger className="h-7 text-xs w-52" data-testid={`tds-party-${b.id}`}>
                                                <SelectValue placeholder="— None —" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none__">— None —</SelectItem>
                                                {tdsParties.filter((p) => p.active).map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name} · {p.section} @ {p.rate_pct}% ({p.pan})</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {tdsPreview ? (
                                            <span className="text-[10px] text-amber-700">
                                                Gross ₹{inr(tdsPreview.gross)} = Net ₹{inr(tdsPreview.net)} + TDS ₹{inr(tdsPreview.tds)} ({tdsPreview.rate}%)
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}
                                {isGateway || b.credit ? (
                                    <div className="flex items-center gap-1">
                                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Gateway gross ₹</Label>
                                        <Input value={gross} onChange={(e) => setGross(e.target.value)} placeholder="e.g. 100 (net was 99)" className="h-7 text-xs w-32" data-testid={`gross-${b.id}`} />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : isPosted && canPost ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => onReverse?.(b)}
                        data-testid={`reverse-${b.id}`}
                        title="Reverse this posting so you can re-map to a different ledger"
                    >
                        Reverse & re-map
                    </Button>
                ) : null}
            </td>
        </tr>
    );
}
